"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ChatStorage = void 0;
const models_1 = require("../models");
const utils_1 = require("../utils");
const LRUQueue_1 = require("./LRUQueue");
class ChatStorage {
    constructor(capacity, networkManager) {
        this.removeListeners = [];
        this.capacity = capacity;
        this.patientChats = this.createGroupQueue();
        this.nonPatientChats = this.createGroupQueue();
        this.hiddenChats = this.createGroupQueue();
        this.privateChats = this.createPrivateQueue();
        this.broadcastChats = this.createBroadcastQueue();
        this.removeListeners.push(networkManager.registerSocketAuthSuccessListener((lastHeartbeatReceivedAt, abortContext) => __awaiter(this, void 0, void 0, function* () {
            const retainChatCount = utils_1.HHNConstants.SYNC_RETAIN_CHAT_COUNT;
            this.patientChats.trim(retainChatCount);
            this.nonPatientChats.trim(retainChatCount);
            this.hiddenChats.trim(retainChatCount);
            this.privateChats.trim(retainChatCount);
            this.broadcastChats.trim(retainChatCount);
            const chats = [
                ...this.patientChats.toArray(),
                ...this.nonPatientChats.toArray(),
                ...this.hiddenChats.toArray(),
                ...this.privateChats.toArray(),
                ...this.broadcastChats.toArray(),
            ];
            yield (0, utils_1.allSettled)(chats.map((c) => __awaiter(this, void 0, void 0, function* () {
                yield c.onSocketAuthSuccess(lastHeartbeatReceivedAt, abortContext);
            })));
        })));
    }
    createGroupQueue() {
        return new LRUQueue_1.LRUQueue(this.capacity, chat => chat.getChatId(), chat => chat.destory());
    }
    createPrivateQueue() {
        return new LRUQueue_1.LRUQueue(this.capacity, chat => chat.getChatId(), chat => chat.destory());
    }
    createBroadcastQueue() {
        return new LRUQueue_1.LRUQueue(this.capacity, chat => chat.getChatId(), chat => chat.destory());
    }
    addChatByType(groupType, chat) {
        if (groupType === models_1.GROUP_TYPE.PATIENT) {
            this.patientChats.add(chat);
        }
        else if (groupType === models_1.GROUP_TYPE.NON_PATIENT) {
            this.nonPatientChats.add(chat);
        }
        else if (groupType === models_1.GROUP_TYPE.PRIVATE) {
            this.privateChats.add(chat);
        }
        else if (groupType === models_1.GROUP_TYPE.HIDDEN) {
            this.hiddenChats.add(chat);
        }
        else if (groupType === models_1.GROUP_TYPE.BROADCAST) {
            this.broadcastChats.add(chat);
        }
    }
    // This is only used when a chat is deleted - need to remove from the cached chats too
    removeChatByType(groupType, chatId) {
        if (groupType === models_1.GROUP_TYPE.PATIENT) {
            this.patientChats.remove(chatId);
        }
        else if (groupType === models_1.GROUP_TYPE.NON_PATIENT) {
            this.nonPatientChats.remove(chatId);
        }
        else if (groupType === models_1.GROUP_TYPE.PRIVATE) {
            this.privateChats.remove(chatId);
        }
        else if (groupType === models_1.GROUP_TYPE.HIDDEN) {
            this.hiddenChats.remove(chatId);
        }
        else if (groupType === models_1.GROUP_TYPE.BROADCAST) {
            this.broadcastChats.remove(chatId);
        }
    }
    touchGroupChatByType(groupType, chatId) {
        let queue;
        switch (groupType) {
            case models_1.GROUP_TYPE.PATIENT:
                queue = this.patientChats;
                break;
            case models_1.GROUP_TYPE.NON_PATIENT:
                queue = this.nonPatientChats;
                break;
            case models_1.GROUP_TYPE.HIDDEN:
                queue = this.hiddenChats;
                break;
        }
        if (!queue) {
            return undefined;
        }
        return queue.touch(chatId);
    }
    getGroupChatByType(groupType, chatId) {
        switch (groupType) {
            case models_1.GROUP_TYPE.PATIENT:
                return this.patientChats.get(chatId);
            case models_1.GROUP_TYPE.NON_PATIENT:
                return this.nonPatientChats.get(chatId);
            case models_1.GROUP_TYPE.HIDDEN:
                return this.hiddenChats.get(chatId);
        }
    }
    getChatInfoByChatType(chatType, chatId) {
        let chat;
        switch (chatType) {
            case models_1.CHAT_TYPE.PRIVATE:
                chat = this.getPrivateChat(chatId);
                break;
            case models_1.CHAT_TYPE.BROADCAST:
                chat = this.getBroadcastChat(chatId);
                break;
            default:
                // eslint-disable-next-line no-case-declarations
                const groupTypes = [
                    models_1.GROUP_TYPE.PATIENT,
                    models_1.GROUP_TYPE.NON_PATIENT,
                    models_1.GROUP_TYPE.HIDDEN,
                ];
                for (const groupType of groupTypes) {
                    const groupChat = this.getGroupChatByType(groupType, chatId);
                    if (groupChat) {
                        chat = groupChat;
                        break;
                    }
                }
        }
        if (chat) {
            return chat.chatInfo;
        }
        throw new Error(`Chat not found ==> Chat Id: ${chatId} - Chat Type: ${chatType}`);
    }
    touchBroadcastChat(chatId) {
        return this.broadcastChats.touch(chatId);
    }
    getBroadcastChat(chatId) {
        return this.broadcastChats.get(chatId);
    }
    touchPrivateChat(chatId) {
        return this.privateChats.touch(chatId);
    }
    getPrivateChat(chatId) {
        return this.privateChats.get(chatId);
    }
    reset() {
        this.patientChats.reset();
        this.nonPatientChats.reset();
        this.hiddenChats.reset();
        this.privateChats.reset();
        this.broadcastChats.reset();
        this.patientChats = this.createGroupQueue();
        this.nonPatientChats = this.createGroupQueue();
        this.hiddenChats = this.createGroupQueue();
        this.privateChats = this.createPrivateQueue();
        this.broadcastChats = this.createBroadcastQueue();
    }
}
exports.ChatStorage = ChatStorage;
